import { Link, LinkType } from "~/common/components/Link";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useLockBodyScroll } from "~/common/hooks/useLockBodyScroll";
import type { SrpProductData } from "~/utils/models";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AutosuggestProductGroup } from "~/domains/navigation/types/NavigationTypes";
import { SEARCH_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";
import { resolveUrl } from "~/utils/url/urlResolver";
import { saveSearchTerm } from "~/features/search/api/saveSearchTerm";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { ProductItem } from "~/features/search/components/Autosuggest/SearchProductResults/ProductItem/ProductItem";
import { Skeleton, Stack, styled, Typography } from "~/common/mui";
import SearchIcon from "@mui/icons-material/Search";

interface SearchProductResultsProps {
  className?: string | false;
  results?: SrpProductData[];
  groups?: AutosuggestProductGroup[];
  state: "loading" | "loaded" | "error";
  query: string;
  hide: () => void;
}

const MAX_PRODUCTS = 5;
const MAX_GROUPS = 3;

const SearchProductResults = ({ results, query, groups, state, hide, className }: SearchProductResultsProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  if (!isDesktop) {
    // TODO: Daniel check this
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useLockBodyScroll();
  }

  const refList = useRef<HTMLAnchorElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const showAllResultsUrl = `${SEARCH_PAGE}?${new URLSearchParams({ text: query }).toString()}`;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex + 1) % refList.current.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((prevIndex) => (prevIndex - 1 + refList.current.length) % refList.current.length);
      } else if (event.key === "Escape") {
        event.preventDefault();
        hide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refList]);

  useEffect(() => {
    if (focusedIndex !== -1) {
      refList.current[focusedIndex].focus();
    }
  }, [focusedIndex]);

  const resultsWithHighlights: (SrpProductData & { highlight: JSX.Element })[] | undefined = useMemo(() => {
    return results?.slice(0, MAX_PRODUCTS).map((item) => ({
      ...item,
      highlight: (
        <TextWithHighlight highlight={query} highlightClass={"f-bold"}>
          {item.designation}
        </TextWithHighlight>
      ),
    }));
  }, [results, query]);

  // const addToCartClick = (e: React.MouseEvent, productCode: string, restrictionQuery: string) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   closeModalById("add-to-cart-dialog");
  //   return AddToCartService.openAddToCartWizard({
  //     productCode: productCode,
  //     restrictionQuery: restrictionQuery,
  //   });
  // };

  return (
    <SearchProductResultContainer className={className || undefined}>
      <Header variant="titleLarge">{t("header.autosuggest.col.product")}</Header>
      <ProductContainer data-zoovu={"searchSuggestions"}>
        {state === "loaded" ? (
          resultsWithHighlights?.map((item, index) => (
            <ProductItem
              key={index}
              product={item}
              query={query}
              hide={hide}
              ref={(el) => {
                if (el) {
                  refList.current[index] = el;
                }
              }}
            />
          ))
        ) : (
          <Skeleton variant="rectangular" />
        )}
      </ProductContainer>

      {groups && groups.length > 0 && (
        <ProductGroupsContainer>
          <ProductGroupsTitle variant="titleLarge">{t("searchProduct.productGroups")}</ProductGroupsTitle>
          {groups?.slice(0, MAX_GROUPS).map((group) => (
            <ProductGroupLink
              key={group.url}
              ref={(el) => {
                if (el && !refList.current.includes(el)) {
                  refList.current.push(el);
                }
              }}
              type="button"
              variant={LinkType.NAVIGATION}
              onClick={() => saveSearchTerm(group.name ?? "", group.url)}
              href={resolveUrl(`${group.url}`)}
            >
              {group.name}
            </ProductGroupLink>
          ))}
        </ProductGroupsContainer>
      )}
      <TotalResults>
        <Link
          href={showAllResultsUrl}
          iconBefore={<SearchIcon  />}
          tracking-aux-sch-show-total-results-link="true"
        >
          {t("searchRelated.showAll")}
        </Link>
      </TotalResults>
    </SearchProductResultContainer>
  );
};

export default SearchProductResults;

const Header = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ProductContainer = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(3),
  [theme.breakpoints.between("tablet", "desktop-s")]: {
    gap: theme.spacing(2),
  },
}));

const ProductGroupsContainer = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const ProductGroupsTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ProductGroupLink = styled(Link)(({ theme }) => ({
  fontWeight: "bold",
  padding: theme.spacing(0.5, 2),
  "&:hover, &:focus-within": {
    background: theme.palette.surfaceContainer.high,
    color: `${theme.palette.text.primary} !important`,
  },
}));

const SearchProductResultContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("tablet")]: {
    overflowY: "scroll",
    minHeight: theme.spacing(112.5),
  },
}));

const TotalResults = styled(Typography)(({ theme }) => ({
  background: theme.palette.surfaceContainer.lowest,
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
  ...theme.typography.titleMedium,
  [theme.breakpoints.up("tablet")]: {
    position: "inherit",
    border: "none",
  },
}));
